// FILE: src/components/admin/NoAccess.jsx
// What a staff member sees on a page (or part of a page) their role doesn't
// allow (RBAC): a lock icon and words, the role's name and a way on, never a
// crash or a blank page. The server refuses the same requests (403
// PERMISSION_DENIED), so this is only the explanation.
//   <PermissionGate need="audit.view">…</PermissionGate>        page-level
//   <PermissionGate need="records.export" headingLevel={2}>…    inside a page with its own h1
//   <PermissionGate need="dashboard.view" redirect>…            send them to a page they can use instead
// `need` is a permission, a list (all) or { anyOf: [...] } (src/auth/permissions.js).
import React from "react";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { Box, Button, Card, CardContent, Typography } from "@mui/material";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import usePermissions from "../../hooks/usePermissions";
import { staffHome } from "../shell/navigation";

export function NoAccessState({ title = "You don't have access to this page", headingLevel = 1, action = true }) {
  const { can, staffRole } = usePermissions();
  const home = staffHome(can);
  return (
    <Card sx={{ borderRadius: 3 }} data-testid="no-access">
      <CardContent sx={{ p: { xs: 3, sm: 4 }, display: "flex", gap: 2, alignItems: "flex-start", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
        <Box
          aria-hidden
          sx={{ flexShrink: 0, width: 48, height: 48, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "zun.primaryTint", color: "primary.main" }}
        >
          <LockPersonOutlinedIcon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component={`h${headingLevel}`} variant={headingLevel === 1 ? "h1" : "h2"} sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: action && home ? 2.5 : 0 }}>
            {staffRole?.name ? `Your role, ${staffRole.name}, doesn't include this.` : "Your role doesn't include this."} Ask an
            administrator if you need it for your work.
          </Typography>
          {action && home && (
            <Button component={RouterLink} to={home} variant="contained">
              Go to a page you can use
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function PermissionGate({ need, children, headingLevel = 1, redirect = false, title }) {
  const { can } = usePermissions();
  if (can(need)) return children;
  if (redirect) {
    const home = staffHome(can);
    if (home) return <Navigate to={home} replace />;
  }
  return <NoAccessState headingLevel={headingLevel} title={title} />;
}
